import styles from "./Time.module.css";
import { warn as logWarn } from "../../../utils/logger";
import { useEffect, useState, useMemo } from "react";
import CloseButton from "../../Button/CloseButton/CloseButton";
import SubmitButton from "../../Button/SubmitButton/SubmitButton";
import {
    generateTimes,
    addMinutesToTime,
    denormalizeDate,
    normalizeDate,
    isDateFarEnough,
    filterDatesOnOrAfterEffectiveTomorrow,
} from "../../../utils/calendar";
import { useTimeModal } from "../../../context/TimeModalContext";
import { useOrderContext } from "../../../context/OrderContext";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { useDropdown } from "../../../hooks/useDropdown";
import { cartChange } from "../../../utils/cart";
import { moveItems } from "../../../services/cartService";
import { getDayHours, isAvailableOn, WEEK_DAY_FULL } from "../../../utils/calendar";
import { DayKey } from "../../../types/product.types";
import { useCartContext } from "../../../context/CartContext";
import { useProducts } from "../../../context/ProductContext";
import { defaultMaxTime, defaultMinTime, defaultOrderTime } from "../../../constants/times";
import { getAdvanceDays } from "../../../utils/products";
import TimeDatePicker from "./TimeDatePicker";

interface TimePopupProps {
    timeOptions?: string[];
    startTime?: string;
    endTime?: string;
    interval?: number;
}

const TimePopup = ({
    timeOptions = [],
    startTime = defaultMinTime,
    endTime = defaultMaxTime,
    interval = 15,
    }: TimePopupProps) => {
    const { t } = useTranslation();
    const { isOpen, close, timeTarget, setTimeTarget, lastTime, setLastTime, openOnTop } = useTimeModal();
    const [selectedTime, setSelectedTime] = useState(defaultOrderTime);
    const { setDeliveryTime, orderData } = useOrderContext();
    const [selectedDate, setSelectedDate] = useState(timeTarget?.date ?? orderData.selectedDate);
    const { cartData, reloadCart } = useCartContext();
    const [options, setOptions] = useState<string[]>(timeOptions);
    const { ref, isOpen: selectOpen, setIsOpen: setSelectOpen } = useDropdown();
    const { ref: dateRef, isOpen: dateOpen, setIsOpen: setDateOpen } = useDropdown();
    const { allProducts } = useProducts();

    const cartDatesISO = useMemo(() => {
        const raw = cartData?.selected_dates ?? [];
        return filterDatesOnOrAfterEffectiveTomorrow(raw.map((d) => normalizeDate(d)));
    }, [cartData?.selected_dates]);

    const dateOptionsISO = cartDatesISO.length > 0
        ? cartDatesISO
        : filterDatesOnOrAfterEffectiveTomorrow(orderData.date);

    // Keep selectedDate valid against filtered options
    useEffect(() => {
        if (!selectedDate) return;
        if (dateOptionsISO.length > 0 && !dateOptionsISO.includes(selectedDate)) {
            setSelectedDate(dateOptionsISO[0]);
        }
    }, [dateOptionsISO, selectedDate]);

    // Generate time options if they arent set
    if (timeOptions.length < 1) {
        timeOptions = generateTimes(interval, startTime, endTime);
    }

    // ---- time helpers ----
    const toMinutes = (time: string | undefined | null): number | null => {
        if (!time) return null;
        const [h, m] = time.split(":").map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) return null;
        return h * 60 + m;
    };

    const isTimeBetween = (time: string, from?: string | null, to?: string | null): boolean => {
        const t = toMinutes(time);
        if (t === null) return false;
        const f = toMinutes(from);
        const tt = toMinutes(to);

        if (f !== null && t < f) return false;
        if (tt !== null && t > tt) return false;
        return true;
    };

    // Items from the group's original date & time
    const groupItems = useMemo(() => {
        if (!cartData || timeTarget?.type !== "group") return [];

        const dateKey = denormalizeDate(timeTarget.date);
        const time = timeTarget.time;

        if (!dateKey || !time) return [];

        return (cartData.items[dateKey] || []).filter((item) => item.time === time);
    }, [cartData, timeTarget]);

    // Helper: check if a single product can be at given date+time
    const canProductBeAt = (
        product: any,
        itemCategory: string,
        date: string,
        startTime: string,
        fullRange: string
    ): boolean => {
        // weekday availability
        if (itemCategory === "sales") {
        const { day } = getDayHours(date);
        const weekday = WEEK_DAY_FULL[day - 1] as DayKey;
        const hasAvailability = isAvailableOn(product, weekday);
        if (!hasAvailability) return false;
        }

        // lead time (order_in_advance)
        if (product.order_in_advance) {
        const advanceDays = getAdvanceDays(product, 0);
        if (advanceDays > 0 && !isDateFarEnough(advanceDays, date)) {
            return false;
        }
        }

        // time of day (order_from / order_to)
        const startOk = isTimeBetween(startTime, product.order_from, product.order_to);
        if (!startOk) return false;

        return true;
    };

    // For group: compute which start times are valid for ALL items in the group
    const validTimesForGroup = useMemo(() => {
        const valid = new Set<string>();

        if (timeTarget?.type !== "group" || !selectedDate) {
        return valid;
        }

        options.forEach((time) => {
        const compTime = `${time} - ${addMinutesToTime(time, 45)}`;

        const okForAll = groupItems.every((item) => {
            const product = allProducts?.find((p: any) => p.id === item.id);
            if (!product) return true;

            return canProductBeAt(product, item.category, selectedDate, time, compTime);
        });

        if (okForAll) {
            valid.add(time);
        }
        });

        return valid;
    }, [options, groupItems, allProducts, timeTarget, selectedDate]);

    // Keep selectedTime valid as long as its start time exists in options
    useEffect(() => {
        if (!selectedTime) return;
        const selectedStart = selectedTime.split(" - ")[0];
        const stillExists = options.includes(selectedStart);

        if (!stillExists && options.length > 0) {
        const first = options[0];
        setSelectedTime(`${first} - ${addMinutesToTime(first, 45)}`);
        }
    }, [options, selectedTime]);

    // Returns the single eligible timeslot on `date` for the product being added,
    // or null if there are zero or more than one eligible slots.
    const getSingleEligibleTime = (date: string): string | null => {
        const dateKey = denormalizeDate(date);
        const itemsOnDate = cartData?.items?.[dateKey] ?? [];
        const product = timeTarget?.productId
            ? allProducts?.find((p: any) => p.id === timeTarget.productId)
            : null;
        const eligible: string[] = [];
        for (const item of itemsOnDate) {
            if (!item.time) continue;
            const itemStart = item.time.split(" - ")[0];
            if (!options.includes(itemStart)) continue;
            if (eligible.includes(item.time)) continue;
            if (product && timeTarget?.prodType) {
                if (!canProductBeAt(product, timeTarget.prodType, date, itemStart, item.time)) continue;
            }
            eligible.push(item.time);
            if (eligible.length > 1) return null;
        }
        return eligible.length === 1 ? eligible[0] : null;
    };

    // Reset base delivery time on display change
    useEffect(() => {
        if (timeTarget?.type === "single") {
        const itemsForDate = cartData?.items[denormalizeDate(timeTarget.date)];
        const prod = itemsForDate?.find(
            (prod) =>
            prod.id === timeTarget.productId &&
            prod.category === timeTarget.prodType &&
            prod.time === timeTarget.time
        );
        if (prod) {
            setSelectedTime(prod.time || `${options[0]} - ${addMinutesToTime(options[0], 45)}`);
        } else {
            const autofill = getSingleEligibleTime(timeTarget.date);
            if (autofill) {
            setSelectedTime(autofill);
            } else if (options.length > 0) {
            setSelectedTime(`${options[0]} - ${addMinutesToTime(options[0], 45)}`);
            } else {
            setSelectedTime(defaultOrderTime);
            }
        }
        } else if (timeTarget?.type === "group") {
        setSelectedTime(timeTarget.time);
        }
    }, [isOpen, options]);

    // Re-autofill when the user switches the date (only for new-item adds)
    useEffect(() => {
        if (!isOpen || timeTarget?.type !== "single") return;
        const itemsForTarget = cartData?.items?.[denormalizeDate(timeTarget.date)];
        const isExistingEdit = itemsForTarget?.some(
            (p) => p.id === timeTarget.productId && p.category === timeTarget.prodType && p.time === timeTarget.time
        );
        if (isExistingEdit) return;
        const autofill = getSingleEligibleTime(selectedDate);
        if (autofill) setSelectedTime(autofill);
    }, [selectedDate]);

    // Set default date on open
    useEffect(() => {
        if (!isOpen || !timeTarget) return;

        const base = timeTarget.date ?? selectedDate ?? orderData.selectedDate;
        setSelectedDate(base);
    }, [isOpen, timeTarget, orderData.selectedDate]);

    // Make sure the target date changes upon opening the popup
    useEffect(() => {
        if (timeTarget?.date === null || timeTarget === null) return;
        if (timeTarget.restrictions) {
        const times = generateTimes(interval, timeTarget.restrictions.min, timeTarget.restrictions.max);
        setOptions(times);
        } else {
        setOptions(timeOptions);
        }
    }, [timeTarget]);

    // ---- GROUP CHANGE: return boolean so caller knows whether to close ----
    const handleGroupChange = async (newTime: string): Promise<boolean> => {
        const date = timeTarget?.date;
        const time = timeTarget?.time;

        if (!date || !time || !cartData) return false;

        const newStart = newTime.split(" - ")[0];

        if (timeTarget?.type === "group" && !validTimesForGroup.has(newStart)) {
        logWarn("Blocked invalid group time change:", newTime);
        return false;
        }

        await moveItems(denormalizeDate(date), time, denormalizeDate(selectedDate), newTime);
        reloadCart();
        setTimeTarget(null);
        return true;
    };

    const itemsForSelectedDate = cartData?.items?.[denormalizeDate(selectedDate)] || [];

    const filteredItems = itemsForSelectedDate.filter((item) => {
        if (!item.time) return false;
        const itemStartTime = item.time.split(" - ")[0];
        return options.includes(itemStartTime);
    });

    const uniqueFilteredItems = filteredItems.filter(
        (item, index, self) => index === self.findIndex((t) => t.time === item.time)
    );

    if (timeTarget === null) return <></>;

    return (
        <>
        {isOpen && (
            <div className={`${styles.Time} ${openOnTop ? styles.TimeOnTop : ""}`}>
            <div className={styles.TimeBackground} onClick={() => close()}></div>
            <div className={styles.TimeCard}>
                <p className={styles.TimeTitle}>{t("time_modal.title")}</p>
                <p className={styles.TimeSubtitle}>{t("time_modal.subtitle")}</p>

                {/* FAST PICKS */}
                <div className={styles.FastPickWrap}>
                {uniqueFilteredItems.map((item, index) => {
                    const itemStart = item.time.split(" - ")[0];
                    const isDisabled = timeTarget?.type === "group" && !validTimesForGroup.has(itemStart);

                    return (
                    <div
                        className={`${styles.FastPickItem} ${isDisabled ? styles.DisabledTime : ""}`}
                        key={`${item.time}-${index}`}
                        onClick={() => {
                        if (isDisabled) return;
                        setSelectedTime(item.time);
                        }}
                    >
                        <p>
                        {denormalizeDate(selectedDate).split(". ").splice(0, 2).join(". ")}. {item.time}
                        </p>
                    </div>
                    );
                })}
                </div>

                {/* TIME PICKER */}
                <div className={styles.TimePickWrapper} ref={ref}>
                <div className={styles.TimePick} onClick={() => setSelectOpen((prev) => !prev)}>
                    {selectedTime && <p>{selectedTime}</p>}
                </div>
                {selectOpen === true && (
                    <div className={styles.PossibleTimes}>
                    {options.map((time) => {
                        const compTime = `${time} - ${addMinutesToTime(time, 45)}`;
                        const isDisabled = timeTarget?.type === "group" && !validTimesForGroup.has(time);

                        return (
                        <div
                            className={`
                            ${styles.TimeOption}
                            ${selectedTime === compTime ? styles.SelectedTime : ""}
                            ${lastTime === compTime ? styles.LastTime : ""}
                            ${isDisabled ? styles.DisabledTime : ""}
                            `}
                            key={compTime}
                            onClick={() => {
                            if (isDisabled) return;
                            setSelectedTime(compTime);
                            setSelectOpen(false);
                            }}
                        >
                            <p>{compTime}</p>
                        </div>
                        );
                    })}
                    </div>
                )}
                </div>

                {/* DATE PICKER */}
                <TimeDatePicker
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    dateOptionsISO={dateOptionsISO}
                    dateOpen={dateOpen}
                    setDateOpen={setDateOpen}
                    dateRef={dateRef}
                    timeTarget={timeTarget}
                    allProducts={allProducts}
                    groupItems={groupItems}
                    setSelectOpen={setSelectOpen}
                />

                <p className={styles.TimeDescTitle}>{t("time_modal.disclaimer")}</p>
                <p className={styles.TimeDescription}>
                <Trans i18nKey="time_modal.example" components={{ 1: <span /> }} />
                </p>

                {timeTarget?.type === "single" ? (
                <SubmitButton
                    text={t("buttons.confirm")}
                    onClick={() => {
                    if (typeof timeTarget?.productId === "number" && cartData) {
                        let amount = 1;

                        const itemsOnDate = cartData.items[timeTarget.date];

                        if (itemsOnDate) {
                        const matchedProduct = itemsOnDate.find(
                            (cartProduct) =>
                            cartProduct.id === timeTarget.productId &&
                            cartProduct.category === timeTarget.prodType &&
                            cartProduct.time === timeTarget.time
                        );

                        if (matchedProduct) {
                            amount = matchedProduct.amount;
                        }
                        }
                        if (timeTarget.amount) {
                        amount = timeTarget.amount;
                        }

                        cartChange(
                        cartData,
                        timeTarget.productId,
                        selectedDate,
                        selectedTime,
                        amount,
                        timeTarget.prodType,
                        timeTarget.date,
                        timeTarget.time,
                        reloadCart
                        );
                    }

                    close();
                    setLastTime(selectedTime);
                    }}
                />
                ) : timeTarget?.type === "group" ? (
                <SubmitButton
                    text={t("buttons.confirm")}
                    onClick={async () => {
                    const ok = await handleGroupChange(selectedTime);
                    if (!ok) return;
                    close();
                    setLastTime(selectedTime);
                    }}
                />
                ) : (
                <SubmitButton
                    text={t("buttons.confirm")}
                    onClick={() => {
                    setDeliveryTime(selectedTime);
                    close();
                    setLastTime(selectedTime);
                    }}
                />
                )}

                <CloseButton onClick={() => close()} className={styles.Close} />
            </div>
            </div>
        )}
        </>
    );
};

export default TimePopup;
